import { useTranslation } from '@/hooks';
import logo from '/logo.png';
import logoWhite from '/logos/white_transparent.png';
import logoColor from '/logos/color_transparent.png';
import logoBlack from '/logos/black_white.png';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from "class-variance-authority";

const logoVariants = cva(
  "flex items-center gap-2",
  {
    variants: {
      variant: {
        default: "",
        centered: "justify-center",
        login: "justify-start gap-3",
      },
      imageVariant: {
        default: "",
        white: "",
        color: "",
        black: "",
      },
    },
    defaultVariants: {
      variant: "default",
      imageVariant: "default",
    },
  }
);

// Logo image mapping
const logoImages = {
  default: logo,
  white: logoWhite,
  color: logoColor,
  black: logoBlack,
} as const;

export interface LogoProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof logoVariants> {
  logoClassName?: string;
  imageVariant?: keyof typeof logoImages;
  showText?: boolean;
}

export function Logo({
  variant,
  imageVariant = "default",
  className,
  logoClassName,
  showText = true,
  ...props
}: LogoProps) {
  const { t } = useTranslation();

  // Determine which logo image to use
  const getLogoImage = () => {
    // Special case for login variant - always use white logo
    if (variant === "login") {
      return logoWhite;
    }

    // Use the specified imageVariant, fallback to default
    return logoImages[imageVariant] || logoImages.default;
  };

  return (
    <div className={cn(logoVariants({ variant, imageVariant, className }))} {...props}>
      <img
        className={cn("img-fluid size-5", logoClassName)}
        src={getLogoImage()}
        alt={t('general.appName')}
      />
      {showText && (
        <span className={cn("text-md font-semibold", variant === "login" ? "text-2xl font-syne font-bold" : "")}>
          {t('general.appName')}
        </span>
      )}
    </div>
  );
}